import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {HttpClient} from '@angular/common/http';
import {AuthenticationService} from '@app/app/shared/services/authentication.service';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/delay';
import {UrlHelper} from '@app/app/shared/helpers/url.helper';

@Component({
  selector: 'employees-dialog',
  templateUrl: 'employees.dialog.html',
  styleUrls: ['employees.dialog.css'],
})

export class EmployeesDialogComponent {
  public addAdminForm: FormGroup;
  public controllers = [];
  public clients = [];

  passwordReset = false;
  resetInProgress = false;
  timerSub = null;
  saveInProgress = false;
  error = '';
  public isAdmin = false;

  constructor(public dialogRef: MatDialogRef<EmployeesDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: any, private http: HttpClient, private auth: AuthenticationService,
              private urlHelper: UrlHelper) {

    this.addAdminForm = new FormGroup({
      first_name: new FormControl('', Validators.required),
      last_name: new FormControl('', Validators.required),
      email: new FormControl('', Validators.required),
      phone: new FormControl(''),
      active: new FormControl(1, Validators.required),
      client: new FormControl('', Validators.required),
      controllers: new FormControl([]),
    });

    if (this.auth.userRole() === 'admin') {
      this.isAdmin = true;

      this.retrieveAvailableClients().subscribe((res: any) => {
        this.clients = res;
      });

      if (data.user) {
        this.addAdminForm.get('client').disable();

        const clientId = data.user.relationships.client.data.id;

        this.retrieveAvailableControllers(clientId).subscribe((res: any) => {
          this.controllers = this.controllers.concat(res);
        });
      }
    } else if (this.auth.userRole() === 'client') {
      this.retrieveAvailableControllers(this.auth.userId()).subscribe((res: any) => {
        this.controllers = this.controllers.concat(res);
      });

      if (!data.user) {
        this.addAdminForm.patchValue({
          client: this.auth.userId()
        });
      }
    }

    if (data.user) {
      this.addAdminForm.patchValue({
        first_name: data.user.attributes.first_name,
        last_name: data.user.attributes.last_name,
        email: data.user.attributes.email,
        phone: data.user.attributes.phone,
        active: data.user.attributes.active,
        client: data.user.relationships.client.data.id,
        controllers: data.user.relationships.parks.data.map(c => c.id),
      });

      this.addAdminForm.get('email').disable();
    }
  }

  private prepareUrl(page, clientId) {
    if (this.auth.userRole() === 'admin') {
      // for admins retrieve unassigned controllers
      return `${this.urlHelper.getPrefix()}/parks?page=${page}&filter[client]=${clientId}`;
    } else if (this.auth.userRole() === 'client') {
      // for clients retrieve their controllers
      return `${this.urlHelper.getPrefix()}/parks?page=${page}`;
    }

    return '';
  }

  private prepareClientsUrl(page) {
      return `/clients?page=${page}`;
  }

  retrieveAvailableClients() {
    return this.http.get(this.prepareClientsUrl(1)).expand((prev: any) => {
      if (typeof prev.meta === 'undefined' || prev.meta.pagination.current_page >= prev.meta.pagination.last_page) {
        return Observable.empty();
      }

      const nextPage = prev.meta.pagination.current_page + 1;

      return this.http.get(this.prepareClientsUrl(nextPage));
    }).scan((acc, r) => acc.concat(r.data), []).last();
  }

  retrieveAvailableControllers(clientId) {
    return this.http.get(this.prepareUrl(1, clientId)).expand((prev: any) => {
      if (typeof prev.meta === 'undefined' || prev.meta.pagination.current_page >= prev.meta.pagination.last_page) {
        return Observable.empty();
      }

      const nextPage = prev.meta.pagination.current_page + 1;

      return this.http.get(this.prepareUrl(nextPage, clientId));
    }).scan((acc, r) => acc.concat(r.data), []).last();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  onCloseConfirm() {
    this.dialogRef.close('Confirm');
  }

  onCloseCancel() {
    this.dialogRef.close('Cancel');
  }

  onSubmit({value, valid}) {
    if (valid) {
      this.saveInProgress = true;
      this.save(value).subscribe(
        (res) => {
          this.saveInProgress = false;
          this.onCloseConfirm();
        }, (err) => {
          this.saveInProgress = false;

          if (err.hasOwnProperty('error') && err.error.hasOwnProperty('errors') && Array.isArray(err.error.errors)) {
            this.error = err.error.errors[0].detail;
          } else {
            this.error = 'An error occurred';
          }
          // do nothing
        }, () => {
          this.saveInProgress = false;
        });
    }
  }

  resetPassword() {
    this.resetInProgress = true;

    if (this.timerSub) {
      this.timerSub.unsubscribe();
    }

    this.auth.forgotPassword(this.data.user.attributes.email).subscribe((res) => {
      this.passwordReset = true;
      this.resetInProgress = false;

      this.timerSub = Observable.of(true).delay(5000).subscribe(() => {
        this.passwordReset = false;
      });
    });
  }

  fetchControllers(event) {
    this.controllers = [];
    const clientId = event.value;

    this.retrieveAvailableControllers(clientId).subscribe((res: any) => {
      this.controllers = this.controllers.concat(res);
    });
  }

  private save(data): Observable<any> {
    const controllers = [];

    data.controllers.forEach(controllerId => {
      controllers.push({type: 'Controllers', id: controllerId});
    });

    const body = {
      data: {
        type: 'Employees',
        attributes: {
          first_name: data.first_name,
          last_name: data.last_name,
          phone: data.phone,
          active: (data.active === 1)
        },
        relationships: {
          client: {
            data: {
              type: 'Clients',
              id: data.client
            },
          },
          controllers: {
            data: controllers
          }
        }
      }
    };

    if (this.data.user) {
      body.data['id'] = this.data.user.id;
      return this.http.put(`${this.urlHelper.getPrefix()}/employees/${this.data.user.id}`, body);
    } else {
      body.data.attributes['email'] = data.email;
      return this.http.post(`${this.urlHelper.getPrefix()}/employees`, body);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/employees/employees.dialog.component.ts